<template>
  <div class="min-h-screen bg-gray-50">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div class="mb-6 flex justify-between items-center">
        <h1 class="text-3xl font-bold text-gray-900">Despesas</h1>
        <button
          @click="openCreateModal"
          class="px-6 py-2.5 rounded-lg transition flex items-center space-x-2 shadow-sm bg-red-600 text-white hover:bg-red-700"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          <span>Nova Despesa</span>
        </button>
      </div>

      <div class="bg-white rounded-lg shadow-sm mb-6">
        <button
          type="button"
          class="w-full flex items-center justify-between px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition"
          @click="showFilters = !showFilters"
        >
          <span class="text-sm font-semibold text-gray-800">Filtros</span>
          <svg
            class="w-5 h-5 text-gray-500 transform transition-transform"
            :class="showFilters ? 'rotate-180' : ''"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div v-if="showFilters" class="px-6 py-4">
          <div class="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-3 items-end">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Imóvel</label>
              <PropertySelectAsync
                v-model="filters.propertyId"
                placeholder="Todos os imóveis"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Proprietário</label>
              <PersonSelectAsync
                v-model="filters.ownerId"
                role="owner"
                placeholder="Todos os proprietários"
                :allow-create="false"
                :show-whats-app-link="false"
              />
            </div>
            <div class="min-w-0 w-full">
              <div class="flex w-full min-w-0 gap-2 items-end">
                <div class="min-w-0 flex-1">
                  <label class="block text-xs font-medium text-gray-600 mb-0.5">Data inicial</label>
                  <input
                    v-model="filters.startDate"
                    type="date"
                    class="w-full min-w-0 text-sm py-1.5 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div class="min-w-0 flex-1">
                  <label class="block text-xs font-medium text-gray-600 mb-0.5">Data final</label>
                  <input
                    v-model="filters.endDate"
                    type="date"
                    class="w-full min-w-0 text-sm py-1.5 px-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Prestador ou Corretor</label>
              <PersonSelectAsync
                v-model="filters.customerId"
                role="provider_or_agent"
                placeholder="Todos os prestadores e corretores"
                :allow-create="false"
                :show-whats-app-link="false"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Classificação da Despesa</label>
              <SelectWithFilter
                v-model="filters.serviceTypeId"
                :options="serviceTypeFilterOptions"
                placeholder="Todos os tipos"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Responsável Gestão</label>
              <SelectWithFilter
                v-model="filters.responsibleId"
                :options="responsibleUserOptions"
                placeholder="Todos os responsáveis"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <SelectWithFilter
                v-model="filters.movementType"
                :options="movementTypeOptions"
                placeholder="Todos os tipos"
              />
            </div>
            <div class="md:col-start-3 flex justify-end">
              <button
                type="button"
                @click="applyFilters"
                class="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center justify-center space-x-2"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Buscar</span>
              </button>
            </div>
          </div>
          <div v-if="dateError" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {{ dateError }}
          </div>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>

      <div v-else class="bg-white rounded-lg shadow-sm overflow-hidden">
        <!-- Mobile: cards -->
        <div class="sm:hidden">
          <div v-if="expenses.length === 0" class="px-6 py-12 text-center text-gray-500">
            Nenhuma despesa cadastrada
          </div>

          <div v-else class="divide-y divide-gray-200">
            <div
              v-for="expense in expenses"
              :key="expense.id"
              class="p-4 bg-white"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p
                    class="text-sm font-semibold text-gray-900 truncate"
                    :title="expenseRowPrestadorTitle(expense)"
                  >
                    {{ truncateAt(expenseRowPrestadorName(expense), 20) || expenseCardLeadFallback(expense) }}
                  </p>
                  <p class="text-xs text-gray-500 mt-1">
                    {{ formatDate(expense.check_in_date) }}
                  </p>
                </div>

                <div
                  v-if="expense.type === 'Expense'"
                  class="flex items-center gap-2 flex-shrink-0"
                >
                  <button
                    @click="editExpense(expense)"
                    class="text-indigo-600 hover:text-indigo-900 p-1"
                    title="Editar"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    @click="confirmDelete(expense)"
                    class="text-red-600 hover:text-red-900 p-1"
                    title="Excluir"
                  >
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
                <span
                  v-else-if="expense.type === 'Service'"
                  class="flex-shrink-0 inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 border border-amber-300 cursor-help"
                  title="Para editar ou excluir, entre no módulo de Reservas."
                >
                  Serviço
                </span>
                <span
                  v-else-if="expense.type === 'Stay'"
                  class="flex-shrink-0 inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-300 cursor-help"
                  title="Para editar ou excluir, entre no módulo de Reservas."
                >
                  Comissão
                </span>
              </div>

              <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                <div class="text-gray-500">Prestador</div>
                <div
                  class="text-gray-900 font-medium truncate"
                  :title="expenseRowPrestadorTitle(expense)"
                >
                  {{ expenseRowPrestadorCell(expense) }}
                </div>

                <div class="text-gray-500">Imóvel</div>
                <div
                  class="text-gray-900 font-medium truncate"
                  :title="expense.property?.name || undefined"
                >
                  {{ truncateAt(expense.property?.name, 20) || '-' }}
                </div>

                <div class="text-gray-500">Descrição</div>
                <div
                  class="text-gray-900 font-medium"
                  :title="expense.description || undefined"
                >
                  {{ truncateAt(expense.description, 30) || '-' }}
                </div>

                <div class="text-gray-500">Classificação</div>
                <div
                  class="text-gray-900 font-medium"
                  :title="expenseRowTipoLabel(expense)"
                >
                  {{ truncateAt(expenseRowTipoLabel(expense), 20) || '-' }}
                </div>

                <div class="text-gray-500">Valor</div>
                <div class="text-red-600 font-semibold text-right tabular-nums">
                  R$ {{ formatCurrency(expenseRowValorAmount(expense)) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: table -->
        <div class="hidden sm:block overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-200">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Data</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Prestador</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Imóvel</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Descrição</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Classificação</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Valor</th>
                <th class="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="expense in expenses" :key="expense.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ formatDate(expense.check_in_date) }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                  :title="expenseRowPrestadorTitle(expense)"
                >
                  {{ expenseRowPrestadorCell(expense) }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-600"
                  :title="expense.property?.name || undefined"
                >
                  {{ truncateAt(expense.property?.name, 20) || '-' }}
                </td>
                <td class="px-6 py-4 text-sm text-gray-600" :title="expense.description">
                  {{ truncateAt(expense.description, 30) || '-' }}
                </td>
                <td
                  class="px-6 py-4 whitespace-nowrap text-sm text-gray-700"
                  :title="expenseRowTipoLabel(expense)"
                >
                  {{ truncateAt(expenseRowTipoLabel(expense), 20) || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-600 text-right tabular-nums">
                  R$ {{ formatCurrency(expenseRowValorAmount(expense)) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                  <div v-if="expense.type === 'Expense'" class="flex space-x-2 justify-end">
                    <button
                      @click="editExpense(expense)"
                      class="text-indigo-600 hover:text-indigo-900"
                      title="Editar"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      @click="confirmDelete(expense)"
                      class="text-red-600 hover:text-red-900"
                      title="Excluir"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                  <span
                    v-else-if="expense.type === 'Service'"
                    class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800 border border-amber-300 cursor-help"
                    title="Para editar ou excluir, entre no módulo de Reservas."
                  >
                    Serviço
                  </span>
                  <span
                    v-else-if="expense.type === 'Stay'"
                    class="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800 border border-blue-300 cursor-help"
                    title="Para editar ou excluir, entre no módulo de Reservas."
                  >
                    Comissão
                  </span>
                </td>
              </tr>
              <tr v-if="expenses.length === 0">
                <td colspan="7" class="px-6 py-12 text-center text-gray-500">
                  Nenhuma despesa cadastrada
                </td>
              </tr>
            </tbody>
            <tfoot v-if="!loading" class="bg-red-50/40 border-t border-gray-200">
              <tr>
                <td colspan="5" class="px-6 py-4 text-left text-sm font-bold text-gray-900">
                  Total das Despesas: {{ pagination.count }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-base font-bold text-red-700 tabular-nums text-right">
                  R$ {{ formatCurrency(filteredTotalAmount) }}
                </td>
                <td class="px-6 py-4"></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div
          v-if="!loading"
          class="sm:hidden px-4 py-3 border-t border-gray-200 bg-red-50/40 flex flex-wrap items-center justify-end gap-2"
        >
          <span class="text-sm font-bold text-gray-900">
            Total das Despesas: {{ pagination.count }}
          </span>
          <span class="text-base font-bold text-red-700 tabular-nums">
            R$ {{ formatCurrency(filteredTotalAmount) }}
          </span>
        </div>

        <div v-if="pagination.count > 0" class="px-6 py-4 border-t border-gray-200 bg-white">
          <div v-if="pagination.pages <= 1" class="flex items-center justify-center">
            <p class="text-sm text-gray-700">
              Total de
              <span class="font-medium">{{ pagination.count }}</span>
              {{ pagination.count === 1 ? 'registro' : 'registros' }}
            </p>
          </div>
          
          <div v-else class="flex items-center justify-between">
            <div class="flex-1 flex justify-between sm:hidden">
              <button
                @click="changePage(pagination.page - 1)"
                :disabled="!pagination.prev"
                class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                @click="changePage(pagination.page + 1)"
                :disabled="!pagination.next"
                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div class="flex items-center space-x-4">
                <p class="text-sm text-gray-700">
                  Mostrando
                  <span class="font-medium">{{ pagination.from }}</span>
                  a
                  <span class="font-medium">{{ pagination.to }}</span>
                  de
                  <span class="font-medium">{{ pagination.count }}</span>
                  resultados
                </p>
              </div>
              <div>
                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    @click="changePage(pagination.page - 1)"
                    :disabled="!pagination.prev"
                    class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  
                  <template v-for="page in visiblePages" :key="page">
                    <button
                      v-if="page !== '...'"
                      @click="changePage(page)"
                      :class="[
                        'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                        page === pagination.page
                          ? 'z-10 bg-red-50 border-red-500 text-red-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      ]"
                    >
                      {{ page }}
                    </button>
                    <span
                      v-else
                      class="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700"
                    >
                      ...
                    </span>
                  </template>

                  <button
                    @click="changePage(pagination.page + 1)"
                    :disabled="!pagination.next"
                    class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isModalOpen || loadingExpenseDetails"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div v-if="loadingExpenseDetails" class="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mb-4"></div>
        <p class="text-gray-600">Carregando dados...</p>
      </div>

      <div v-else class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 bg-white">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ editingExpense ? 'Editar Despesa' : 'Nova Despesa' }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form @submit.prevent="saveExpense" class="px-6 py-4 overflow-y-auto flex-1 flex flex-col">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <PersonSelectAsync
                v-model="form.customer_id"
                :selected-person="selectedProvider"
                label="Prestador Serviço *"
                role="provider"
                placeholder="Selecione ou digite para buscar..."
                @update:modelValue="handleProviderChange"
                required
              />
            </div>

            <div>
              <PropertySelectAsync
                v-model="form.property_id"
                :selected-property="selectedExpenseProperty"
                label="Imóvel *"
                placeholder="Selecione um imóvel..."
                required
              />
            </div>

            <div class="md:col-span-2">
              <ServiceTypeSelectAsync
                v-model="form.service_type_id"
                :selected-service-type="selectedExpenseServiceType"
                label="Classificação da Despesa *"
                placeholder="Selecione um tipo de despesa..."
                @update:modelValue="handleServiceTypeChange"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Data *</label>
              <input
                v-model="form.check_in_date"
                type="date"
                required
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Valor da despesa (R$) *</label>
              <input
                v-model="form.total_payable"
                type="text"
                @input="handleCurrencyInput($event, 'total_payable')"
                required
                placeholder="0,00"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>            

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">Descrição/Comentário</label>
              <textarea
                v-model="form.description"
                rows="3"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                placeholder="Detalhes sobre a despesa..."
              ></textarea>
            </div>

            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-2">Anexos</label>
              <AttachmentManager
                v-model="form.attachments"
                v-model:remove-attachment-ids="form.remove_attachment_ids"
                v-model:attachments-order="form.attachments_order"
                :accepted-types="'*'"
                :enable-reorder="true"
                :enable-lightbox="true"
              />
            </div>
          </div>
        </form>

        <div class="px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-white flex justify-between items-center relative">
          <div v-if="formErrors.general" class="absolute left-6 -top-12 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg w-[calc(100%-3rem)] flex items-start justify-between">
            <span class="flex-1">{{ formErrors.general }}</span>
            <button 
              @click="formErrors.general = ''"
              class="ml-3 text-red-700 hover:text-red-900 flex-shrink-0"
              type="button"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <button
            v-if="editingExpense"
            type="button"
            @click="deleteConfirmExpense = editingExpense"
            class="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            Excluir
          </button>
          <div v-else></div>
          <div class="flex space-x-3">
            <button
              type="button"
              :disabled="saving"
              @click="openSaveConfirm"
              class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
            >
              {{ saving ? 'Salvando...' : 'Salvar' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
            >
              Sair
            </button>            
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="saveConfirmOpen"
      class="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
      @click.self="closeSaveConfirm"
    >
      <div class="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <h3 class="flex items-center text-lg font-semibold text-gray-900 mb-4">
          <svg class="w-6 h-6 text-amber-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59C19.02 16.92 18.145 18 16.757 18H3.243c-1.388 0-2.263-1.08-1.504-2.31l6.518-11.59zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-.894.553L9 7v4a1 1 0 002 0V7l-.106-.447A1 1 0 0010 6z" clip-rule="evenodd" />
          </svg>
          Confirmação!
        </h3>
        <p class="text-gray-600 mb-6">
          Deseja salvar esta despesa?
        </p>
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            :disabled="saving"
            @click="confirmSave"
            class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
          >
            {{ saving ? 'Salvando...' : 'Sim' }}
          </button>
          <button
            type="button"
            @click="closeSaveConfirm"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Não
          </button>
        </div>
      </div>
    </div>

    <ConfirmationModal
      :isOpen="!!deleteConfirmExpense"
      title="Confirmação!"
      message="Tem certeza que deseja excluir esta despesa?"
      :loading="deleting"
      @confirm="deleteExpense"
      @cancel="deleteConfirmExpense = null"
    />

    <QuickPersonModal
      :isOpen="quickPersonModalOpen.isOpen"
      :personType="quickPersonModalOpen.type"
      @save="handleQuickPersonSave"
      @close="closeQuickPersonModal"
    />

    <QuickServiceTypeModal
      :isOpen="quickServiceTypeModalOpen"
      @save="handleQuickServiceTypeSave"
      @close="closeQuickServiceTypeModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import ConfirmationModal from './ConfirmationModal.vue'
import PersonSelectAsync from './PersonSelectAsync.vue'
import PropertySelectAsync from './PropertySelectAsync.vue'
import AttachmentManager from './AttachmentManager.vue'
import QuickPersonModal from './QuickPersonModal.vue'
import QuickServiceTypeModal from './QuickServiceTypeModal.vue'
import ServiceTypeSelectAsync from './ServiceTypeSelectAsync.vue'
import SelectWithFilter from './SelectWithFilter.vue'
import { useBrazilianMasks } from '../composables/useBrazilianMasks.js'
import { useApi } from '../composables/useApi.js'

export default {
  components: {
    ConfirmationModal,
    PersonSelectAsync,
    PropertySelectAsync,
    AttachmentManager,
    QuickPersonModal,
    QuickServiceTypeModal,
    ServiceTypeSelectAsync,
    SelectWithFilter
  },

  data() {
    const { applyCurrencyMask, parseCurrencyToNumber } = useBrazilianMasks()
    const today = new Date()
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    const startDateDefault = firstDayOfMonth.toISOString().split('T')[0]
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    const endDateDefault = lastDayOfMonth.toISOString().split('T')[0]

    return {
      applyCurrencyMask,
      parseCurrencyToNumber,
      showFilters: true,
      filters: {
        propertyId: '',
        ownerId: '',
        responsibleId: '',
        customerId: '',
        serviceTypeId: '',
        movementType: '',
        startDate: startDateDefault,
        endDate: endDateDefault
      },
      serviceTypeFilterOptions: [{ value: '', label: 'Todos os tipos' }],
      movementTypeOptions: [
        { value: '', label: 'Todos os tipos' },
        { value: 'service', label: 'Serviços' },
        { value: 'expense', label: 'Despesas' },
        { value: 'commission', label: 'Comissão' }
      ],
      users: [],
      dateError: '',
      expenses: [],
      filteredTotalAmount: 0,
      loading: false,
      loadingExpenseDetails: false,
      isModalOpen: false,
      editingExpense: null,
      saving: false,
      saveConfirmOpen: false,
      deleteConfirmExpense: null,
      deleting: false,
      formErrors: {},
      form: this.getEmptyForm(),
      quickPersonModalOpen: {
        isOpen: false,
        type: null
      },
      quickServiceTypeModalOpen: false,
      pagination: {
        page: 1,
        items: 20,
        count: 0,
        pages: 0,
        from: 0,
        to: 0,
        prev: null,
        next: null
      }
    }
  },

  computed: {
    selectedProvider() {
      if (!this.editingExpense || !this.editingExpense.customer) return null
      return this.editingExpense.customer
    },
    selectedExpenseProperty() {
      if (!this.editingExpense || !this.editingExpense.property) return null
      return this.editingExpense.property
    },
    selectedExpenseServiceType() {
      if (!this.editingExpense || !this.editingExpense.service_type) return null
      return this.editingExpense.service_type
    },

    visiblePages() {
      const current = this.pagination.page
      const total = this.pagination.pages
      const delta = 2
      const range = []
      const rangeWithDots = []

      for (let i = Math.max(2, current - delta); i <= Math.min(total - 1, current + delta); i++) {
        range.push(i)
      }

      if (current - delta > 2) {
        rangeWithDots.push(1, '...')
      } else {
        rangeWithDots.push(1)
      }

      rangeWithDots.push(...range)

      if (current + delta < total - 1) {
        rangeWithDots.push('...', total)
      } else if (total > 1) {
        rangeWithDots.push(total)
      }

      return rangeWithDots
    },

    responsibleUserOptions() {
      return [
        { value: '', label: 'Todos os responsáveis' },
        ...(this.users || []).map(user => ({
          value: user.id,
          label: `${user.first_name} ${user.last_name}`.trim() || user.email
        }))
      ]
    }
  },

  mounted() {
    this.loadResponsibleUsers()
    this.loadServiceTypeFilterOptions()
    this.loadExpenses()
  },

  methods: {
    async loadResponsibleUsers() {
      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const response = await axios.get('/api/v1/users', {
          headers: { 
            Authorization: `Bearer ${userToken}`, 
            'Tenant-Authorization': `Bearer ${tenantToken}` 
          }
        })

        this.users = response.data?.data || response.data || []
      } catch (error) {
        console.error('Erro ao carregar usuários para filtro de responsável:', error)
        this.users = []
      }
    },

    async loadServiceTypeFilterOptions() {
      const userToken = localStorage.getItem('userToken')
      const tenantToken = localStorage.getItem('tenantToken')
      const headers = {
        Authorization: `Bearer ${userToken}`,
        'Tenant-Authorization': `Bearer ${tenantToken}`
      }
      const collected = []
      try {
        let page = 1
        let pages = 1
        do {
          const { data } = await axios.get('/api/v1/service_types', { params: { page }, headers })
          const rows = Array.isArray(data?.data) ? data.data : []
          collected.push(...rows)
          pages = data?.pagy?.pages ?? 1
          page += 1
        } while (page <= pages)
      } catch (error) {
        console.error('Erro ao carregar as classificações de despesa para filtro:', error)
      }
      this.serviceTypeFilterOptions = [
        { value: '', label: 'Todos as classificações' },
        ...collected.map(st => ({ value: String(st.id), label: st.name }))
      ]
    },

    openSaveConfirm() {
      this.saveConfirmOpen = true
    },

    getEmptyForm() {
      return {
        customer_id: '',
        property_id: '',
        service_type_id: '',
        check_in_date: '',
        total_payable: '0,00',
        description: '',
        attachments: [],
        remove_attachment_ids: [],
        attachments_order: []
      }
    },

    applyFilters() {
      this.pagination.page = 1
      if (!this.validateDateRange()) return
      this.loadExpenses()
    },

    validateDateRange() {
      const start = this.filters.startDate
      const end = this.filters.endDate

      if (!start || !end) {
        this.dateError = 'Informe data inicial e data final para pesquisar.'
        return false
      }

      const startDate = new Date(start)
      const endDate = new Date(end)

      if (isNaN(startDate) || isNaN(endDate)) {
        this.dateError = 'Datas inválidas.'
        return false
      }

      if (endDate < startDate) {
        this.dateError = 'A data final não pode ser menor que a data inicial.'
        return false
      }

      const maxEndDate = new Date(startDate)
      maxEndDate.setFullYear(maxEndDate.getFullYear() + 1)

      if (endDate > maxEndDate) {
        this.dateError = 'O período máximo é de 1 ano.'
        return false
      }

      this.dateError = ''
      return true
    },

    async loadExpenses() {
      this.loading = true
      try {
        if (!this.validateDateRange()) {
          this.loading = false
          return
        }

        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')

        const params = { page: this.pagination.page }
        if (this.filters.propertyId) params.property_id = this.filters.propertyId
        if (this.filters.ownerId) params.owner_id = this.filters.ownerId
        if (this.filters.responsibleId) params.responsible_id = this.filters.responsibleId
        if (this.filters.customerId) params.customer_id = this.filters.customerId
        if (this.filters.serviceTypeId) params.service_type_id = this.filters.serviceTypeId
        if (this.filters.movementType) params.movement_type = this.filters.movementType
        if (this.filters.startDate) params.start_date = this.filters.startDate
        if (this.filters.endDate) params.end_date = this.filters.endDate

        const queryString = new URLSearchParams(params).toString()
        const url = `/api/v1/expenses?${queryString}`
        
        const response = await axios.get(url, {
          headers: { 
            Authorization: `Bearer ${userToken}`, 
            'Tenant-Authorization': `Bearer ${tenantToken}` 
          }
        })
        
        if (response.data.data && response.data.pagy) {
          this.expenses = Array.isArray(response.data.data) ? response.data.data : []
          this.pagination = response.data.pagy
          this.filteredTotalAmount =
            typeof response.data.total_amount === 'number' ? response.data.total_amount : 0
        } else {
          this.expenses = Array.isArray(response.data) ? response.data : []
          this.filteredTotalAmount = 0
        }
      } catch (err) {
        console.error('Error loading expenses:', err)
      } finally {
        this.loading = false
      }
    },

    changePage(page) {
      this.pagination.page = page
      this.loadExpenses()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    truncateAt(text, length) {
      if (text == null || text === '') return ''
      const s = String(text)
      return s.length > length ? `${s.slice(0, length)}...` : s
    },

    expenseIsStayCommission(expense) {
      return expense && expense.type === 'Stay'
    },

    expenseCardLeadFallback(expense) {
      return this.expenseIsStayCommission(expense) ? 'Corretor não informado' : 'Prestador não informado'
    },

    expenseRowPrestadorName(expense) {
      if (this.expenseIsStayCommission(expense)) return expense.seller?.name || ''
      return expense.customer?.name || ''
    },

    expenseRowPrestadorTitle(expense) {
      const n = this.expenseRowPrestadorName(expense)
      return n || undefined
    },

    expenseRowPrestadorCell(expense) {
      return this.truncateAt(this.expenseRowPrestadorName(expense), 20) || '-'
    },

    expenseRowValorAmount(expense) {
      if (this.expenseIsStayCommission(expense)) {
        const v = expense.total_payable ?? expense.total_price
        return v != null && v !== '' ? v : 0
      }
      return expense.total_payable || expense.total_price || 0
    },

    expenseRowTipoLabel(expense) {
      if (this.expenseIsStayCommission(expense)) return 'Comissão'
      return expense.service_type?.name || '-'
    },

    formatDate(dateString) {
      const [year, month, day] = dateString.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('pt-BR')
    },

    formatCurrency(value) {
      const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : parseFloat(value || 0)
      return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },

    handleCurrencyInput(event, fieldName) {
      const cursorPosition = event.target.selectionStart
      const oldValue = event.target.value
      const oldLength = oldValue.length
      
      const newValue = this.applyCurrencyMask(event.target.value)
      
      this.form[fieldName] = newValue
      
      this.$nextTick(() => {
        const newLength = newValue.length
        const diff = newLength - oldLength
        const newCursorPosition = Math.max(0, cursorPosition + diff)
        event.target.setSelectionRange(newCursorPosition, newCursorPosition)
      })
    },

    async openCreateModal() {
      this.editingExpense = null
      this.form = this.getEmptyForm()
      this.formErrors = {}
      this.isModalOpen = true
    },

    async editExpense(expense) {
      if (expense.type !== 'Expense') return

      this.loadingExpenseDetails = true
      this.formErrors = {}

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        
        const response = await axios.get(`/api/v1/expenses/${expense.id}`, {
          headers: { 
            Authorization: `Bearer ${userToken}`, 
            'Tenant-Authorization': `Bearer ${tenantToken}` 
          }
        })
        
        this.editingExpense = response.data
        
        const formatMoneyValue = (value) => {
          const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : parseFloat(value || 0)
          return this.formatCurrency(numValue)
        }
        
        this.form = {
          ...this.getEmptyForm(),
          ...response.data,
          customer_id: response.data.customer_id ? String(response.data.customer_id) : '',
          property_id: response.data.property_id || '',
          service_type_id: response.data.service_type?.id
            ? String(response.data.service_type.id)
            : (response.data.service_type_id ? String(response.data.service_type_id) : ''),
          total_payable: formatMoneyValue(response.data.total_payable || response.data.total_price),
          attachments: response.data.attachments || [],
          attachments_order: response.data.attachments_order || []
        }

        this.isModalOpen = true
      } catch (err) {
        this.formErrors.general = err.response?.data?.error || 'Erro ao carregar os dados'
      } finally {
        this.loadingExpenseDetails = false
      }
    },

    closeModal() {
      if (this.form.attachments) {
        this.form.attachments.forEach(item => {
          const isFile = (typeof File !== 'undefined' && item instanceof File) ||
            (typeof Blob !== 'undefined' && item instanceof Blob)
          if (isFile && item.previewUrl) {
            URL.revokeObjectURL(item.previewUrl)
          }
        })
      }
      
      this.isModalOpen = false
      this.editingExpense = null
      this.form = this.getEmptyForm()
      this.formErrors = {}
      this.loadExpenses()
    },

    confirmSave() {
      this.saveConfirmOpen = false
      this.saveExpense()
    },

    closeSaveConfirm() {
      this.saveConfirmOpen = false
    },

    parseExpenseId(value) {
      if (value === '' || value === null || value === undefined || value === 'new') return null
      const n = parseInt(String(value), 10)
      return Number.isNaN(n) ? null : n
    },

    buildExpenseFormData() {
      const isFile = (item) => (typeof File !== 'undefined' && item instanceof File) ||
        (typeof Blob !== 'undefined' && item instanceof Blob)
      const newFiles = (this.form.attachments || []).filter(item => isFile(item))
      const hasNewFiles = newFiles.length > 0
      const hasRemovedAttachments = this.form.remove_attachment_ids && this.form.remove_attachment_ids.length > 0
      const hasAttachmentsOrder = this.form.attachments_order && this.form.attachments_order.length > 0

      const expenseData = {
        customer_id: this.form.customer_id,
        property_id: this.form.property_id || null,
        service_type_id: this.parseExpenseId(this.form.service_type_id),
        check_in_date: this.form.check_in_date,
        check_out_date: this.form.check_in_date,
        total_price: 0,
        total_due: 0,
        balance_due: 0,
        total_payable: this.parseCurrencyToNumber(this.form.total_payable || '0'),
        total_paid: this.parseCurrencyToNumber(this.form.total_payable || '0'),
        balance_payable: this.parseCurrencyToNumber(this.form.total_payable || '0'),
        description: this.form.description || ''
      }

      // Sempre incluir attachments_order se houver (independente de novos arquivos)
      if (hasAttachmentsOrder) {
        expenseData.attachments_order = this.form.attachments_order
      }

      if (!hasNewFiles && !hasRemovedAttachments && !hasAttachmentsOrder) {
        return { useFormData: false, payload: expenseData }
      }

      if (!hasNewFiles) {
        if (hasRemovedAttachments) {
          expenseData.remove_attachment_ids = this.form.remove_attachment_ids
        }
        return { useFormData: false, payload: expenseData }
      }

      const formData = new FormData()
      
      Object.keys(expenseData).forEach(key => {
        const value = expenseData[key]
        if (value !== null && value !== undefined && value !== '') {
          formData.append(`expense[${key}]`, value)
        }
      })

      newFiles.forEach((file) => {
        formData.append(`expense[attachments][]`, file, file.name)
      })

      if (hasRemovedAttachments) {
        this.form.remove_attachment_ids.forEach(id => {
          formData.append(`expense[remove_attachment_ids][]`, id)
        })
      }

      if (this.form.attachments_order && this.form.attachments_order.length > 0) {
        this.form.attachments_order.forEach(id => {
          formData.append(`expense[attachments_order][]`, id)
        })
      }

      return { useFormData: true, payload: formData }
    },

    async saveExpense() {
      this.saving = true
      this.formErrors = {}

      if (!this.form.service_type_id || this.form.service_type_id === 'new') {
        this.formErrors.general = 'Selecione a classificação da despesa.'
        this.saving = false
        return
      }

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        const headers = { 
          Authorization: `Bearer ${userToken}`, 
          'Tenant-Authorization': `Bearer ${tenantToken}` 
        }

        const { useFormData, payload } = this.buildExpenseFormData()

        if (this.editingExpense) {
          if (useFormData) {
            payload.append('_method', 'PATCH')
            await axios.post(`/api/v1/expenses/${this.editingExpense.id}`, payload, { headers })
          } else {
            await axios.patch(`/api/v1/expenses/${this.editingExpense.id}`, { expense: payload }, { headers })
          }
        } else {
          if (useFormData) {
            await axios.post('/api/v1/expenses', payload, { headers })
          } else {
            await axios.post('/api/v1/expenses', { expense: payload }, { headers })
          }
        }

        this.closeModal()
      } catch (err) {
        this.formErrors.general = err.response?.data?.errors?.join(', ') || 'Erro ao salvar despesa'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(expense) {
      if (expense.type !== 'Expense') return
      this.deleteConfirmExpense = expense
    },

    async deleteExpense() {
      this.deleting = true

      try {
        const userToken = localStorage.getItem('userToken')
        const tenantToken = localStorage.getItem('tenantToken')
        
        await axios.delete(`/api/v1/expenses/${this.deleteConfirmExpense.id}`, {
          headers: { 
            Authorization: `Bearer ${userToken}`, 
            'Tenant-Authorization': `Bearer ${tenantToken}` 
          }
        })

        this.deleteConfirmExpense = null
        this.closeModal()
      } catch (err) {
        console.error('Error deleting expense:', err)
        this.deleteConfirmExpense = null
      } finally {
        this.deleting = false
      }
    },

    handleProviderChange(value) {
      if (value === 'new') {
        this.quickPersonModalOpen = { isOpen: true, type: 'Provider' }
        this.form.customer_id = ''
      }
    },

    async handleQuickPersonSave(person) {
      this.form.customer_id = String(person.id)
      this.closeQuickPersonModal()
    },

    closeQuickPersonModal() {
      this.quickPersonModalOpen = { isOpen: false, type: null }
    },

    handleServiceTypeChange(value) {
      if (value === 'new') {
        this.quickServiceTypeModalOpen = true
        this.form.service_type_id = ''
      }
    },

    handleQuickServiceTypeSave(serviceType) {
      if (serviceType) {
        this.form.service_type_id = String(serviceType.id)
        const { getServiceTypes } = useApi()
        getServiceTypes()
      }
      this.closeQuickServiceTypeModal()
    },

    closeQuickServiceTypeModal() {
      this.quickServiceTypeModalOpen = false
    }
  }
}
</script>
